import { useEffect, useRef, useState } from "react";

import PropTypes from "prop-types";

import { annotationPropType } from "@/view/viewPropTypes";
import ErrorIcon from "@/icons/ErrorIcon";
import AnnotationCommentActions from "../Actions/AnnotationCommentActions";
import AnnotationComment from "../AnnotationComment";
import AnnotationTextArea from "../AnnotationTextArea";
import {
  rootCss,
  noCommentErrorMessageCss,
  noCommentErrorTextCss,
  noCommentErrorSvgCss,
} from "./AnnotationCommentThread.style";

const propTypes = {
  annotation: annotationPropType,
  commentThreadProps: PropTypes.shape({
    commentThread: PropTypes.array,
    lastCommentIsNotTheirOwn: PropTypes.bool,
    latestCommentInThread: PropTypes.object,
    numberOfCommentsInThread: PropTypes.number,
  }),
  deleteAnnotation: PropTypes.func,
  getAnnotations: PropTypes.func,
  handleAnnotationActionsRequest: PropTypes.func,
  isActionPending: PropTypes.bool,
  isDraft: PropTypes.bool,
  legacyArticleId: PropTypes.number,
  saveAnnotation: PropTypes.func,
};

const AnnotationCommentThread = ({
  annotation,
  commentThreadProps,
  deleteAnnotation,
  getAnnotations,
  handleAnnotationActionsRequest,
  isActionPending,
  isDraft,
  legacyArticleId,
  saveAnnotation,
}) => {
  const {
    commentThread,
    lastCommentIsNotTheirOwn,
    latestCommentInThread,
    numberOfCommentsInThread,
  } = commentThreadProps;

  const [noCommentError, setNoCommentError] = useState(false);
  const [shouldShowDeleteConfirmation, setShouldShowDeleteConfirmation] =
    useState(false);
  const [currentComment, setCurrentComment] = useState({
    ...latestCommentInThread,
  });
  const [isCommentEditMode, setIsCommentEditMode] = useState(isDraft);
  const commentTextAreaRef = useRef();
  const commentDeleteConfirmationButtonRef = useRef();
  const commentDeleteButtonRef = useRef();

  const clearCommentText = () => setCurrentComment(null);

  useEffect(() => {
    setCurrentComment({
      ...latestCommentInThread,
      text: lastCommentIsNotTheirOwn ? "" : latestCommentInThread?.text,
    });
  }, [latestCommentInThread]);

  useEffect(() => {
    if (noCommentError) {
      commentTextAreaRef?.current?.focus();
    }
  }, [noCommentError]);

  const handleCancelComment = () => {
    if (currentComment) {
      clearCommentText();
      commentTextAreaRef?.current?.focus();
    }
  };

  const handleEmptyTextArea = (textValue) => {
    if (!textValue) {
      setNoCommentError(true);
    }
  };

  const handleDeleteComment = async () => {
    const { error } = await handleAnnotationActionsRequest(
      deleteAnnotation,
      currentComment?.id,
      getAnnotations,
    );
    if (!error) {
      setIsCommentEditMode(false);
      setShouldShowDeleteConfirmation(false);

      setTimeout(() => {
        commentTextAreaRef?.current?.focus();
      }, 50);
    }
  };

  const showDeleteConfirmation = () => {
    setShouldShowDeleteConfirmation(true);
    setTimeout(() => {
      commentDeleteConfirmationButtonRef?.current?.focus();
    }, 50);
  };

  const handleEditComment = () => {
    setIsCommentEditMode(true);
    setNoCommentError(false);

    setTimeout(() => {
      commentTextAreaRef?.current?.focus();
    }, 50);
  };

  const handleSubmitEditedComment = async () => {
    handleEmptyTextArea(currentComment?.text);

    if (currentComment?.text) {
      const { error } = await handleAnnotationActionsRequest(
        saveAnnotation,
        currentComment,
        getAnnotations,
      );

      if (!error) {
        setIsCommentEditMode(false);
      }
    }
  };

  const handleCancel = () => {
    setCurrentComment(latestCommentInThread);
    setIsCommentEditMode(false);
    setShouldShowDeleteConfirmation(false);
    setTimeout(() => {
      commentDeleteButtonRef?.current?.focus();
    }, 50);
  };

  const handleTextChangeForNewComment = (e) => {
    const mostRecentAnnotation = latestCommentInThread || annotation;

    if (e.target.value === "") {
      clearCommentText();
    }
    if (isCommentEditMode) {
      setCurrentComment({ ...currentComment, text: e.target.value });
      setNoCommentError(false);
    } else {
      setNoCommentError(false);
      setCurrentComment({
        article_id: legacyArticleId,
        text: e.target.value,
        in_response_to: { id: mostRecentAnnotation?.id },
        category: "user",
        start: mostRecentAnnotation?.start,
        startoffset: mostRecentAnnotation?.startoffset,
        end: mostRecentAnnotation?.end,
        endoffset: mostRecentAnnotation?.endoffset,
      });
    }
  };

  const handleSubmitComment = async () => {
    handleEmptyTextArea(currentComment?.text);

    if (currentComment?.text) {
      const { data, error } = await handleAnnotationActionsRequest(
        saveAnnotation,
        currentComment,
        getAnnotations,
      );
      if (!error) {
        setCurrentComment(data);
        setIsCommentEditMode(false);
      }
    }
  };

  const commentActionProps = {
    annotationId: annotation.id,
    commentDeleteConfirmationButtonRef,
    commentDeleteButtonRef,
    isActionPending,
    isLastCommentInThread: true,
    isCommentEditMode,
    handleCancel,
    handleDelete: handleDeleteComment,
    handleEdit: handleEditComment,
    handleSave: handleSubmitEditedComment,
    showDeleteConfirmation,
  };

  const renderActions = () => {
    return (
      <AnnotationCommentActions
        annotationId={annotation.id}
        currentComment={currentComment}
        handleCancel={handleCancelComment}
        handleSave={handleSubmitComment}
        isActionPending={isActionPending}
        isReply
      />
    );
  };

  const renderNoCommentErrorMessage = () => (
    <div css={noCommentErrorMessageCss}>
      <ErrorIcon css={noCommentErrorSvgCss} />
      <span id="noCommentError" css={noCommentErrorTextCss}>
        Enter reply
      </span>
    </div>
  );

  const renderAnnotationCommentTextArea = () => {
    if (isCommentEditMode || lastCommentIsNotTheirOwn) {
      return (
        <form>
          <AnnotationTextArea
            handleOnChange={handleTextChangeForNewComment}
            isCommentEditMode={isCommentEditMode}
            isReply
            noCommentError={noCommentError}
            rows={1}
            text={currentComment?.text || ""}
            annotationTextAreaRef={commentTextAreaRef}
          />
          {noCommentError ? renderNoCommentErrorMessage() : null}
          {isCommentEditMode ? null : renderActions()}
        </form>
      );
    }
  };

  const renderCommentThread = () => {
    return commentThread?.map((comment, index) => {
      const shouldShowCommentActions =
        !lastCommentIsNotTheirOwn && numberOfCommentsInThread - 1 === index;
      return (
        <AnnotationComment
          key={comment.id}
          commentActionProps={
            shouldShowCommentActions ? commentActionProps : null
          }
          firstName={comment.user.first_name}
          lastName={comment.user.last_name}
          shouldShowDeleteConfirmation={shouldShowDeleteConfirmation}
          text={comment.text}
        />
      );
    });
  };

  return (
    <div css={rootCss}>
      {renderCommentThread()}
      {renderAnnotationCommentTextArea()}
    </div>
  );
};

AnnotationCommentThread.propTypes = propTypes;
export default AnnotationCommentThread;
